/* =====================================================================
   Machmani custom JS for Shopify Horizon (ES module)
   Loaded in layout/theme.liquid (after Horizon's import map).
   Works together with Horizon's cart drawer through the standard
   CartLinesUpdateEvent, so the drawer opens/refreshes after every add.
   ===================================================================== */
import { CartLinesUpdateEvent } from '@shopify/events';

const $ = (s, r = document) => r.querySelector(s);
const $$ = (s, r = document) => [...r.querySelectorAll(s)];
const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const R = (window.Theme && window.Theme.routes) || {};
const URLS = {
  add: `${(R.cart_add_url || `${ROOT}cart/add`).replace(/\.js$/, '')}.js`,
  cart: `${R.cart_url || `${ROOT}cart`}.js`,
  update: `${(R.cart_update_url || `${ROOT}cart/update`).replace(/\.js$/, '')}.js`,
  checkout: `${ROOT}checkout`,
};

/* ---------------- Toast ---------------- */
function toast(msg) {
  let t = $('.mm-toast');
  if (!t) { t = document.createElement('div'); t.className = 'mm-toast'; t.setAttribute('role', 'status'); document.body.appendChild(t); }
  t.textContent = msg; t.classList.add('is-on');
  clearTimeout(t._t); t._t = setTimeout(() => t.classList.remove('is-on'), 2600);
}
window.mmToast = toast;

/* ---------------- Cart ---------------- */
const getCart = () => fetch(URLS.cart, { headers: { Accept: 'application/json' }, credentials: 'same-origin' }).then((r) => r.json());

/**
 * Add one or more variants to the cart and let Horizon's cart drawer update itself.
 * @param {{id:string|number, quantity?:number}[]} items
 * @param {Element} source
 */
async function mmAddToCart(items, source = document.body) {
  const sectionIds = $$('cart-items-component').map((e) => e.dataset.sectionId).filter(Boolean);
  const itemCount = items.reduce((a, i) => a + (i.quantity || 1), 0);
  const deferred = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(new CartLinesUpdateEvent({
    action: 'add',
    context: 'product',
    lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity || 1 })),
    promise: deferred.promise,
  }));

  let data;
  try {
    const body = { items: items.map((i) => ({ id: Number(i.id), quantity: i.quantity || 1 })) };
    if (sectionIds.length) { body.sections = sectionIds.join(','); body.sections_url = window.location.pathname; }
    const res = await fetch(URLS.add, {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    data = await res.json();
  } catch (err) {
    deferred.reject(err);
    toast('Network error. Please try again.');
    throw err;
  }

  const cart = await getCart().catch(() => null);
  const failed = Boolean(data && data.status);
  if (cart) {
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, source: 'machmani-theme', itemCount, sections: failed ? undefined : data.sections, didError: failed },
    });
  } else {
    deferred.reject(new Error('cart fetch failed'));
  }
  if (failed) {
    const msg = data.description || data.message || 'Could not add to cart';
    toast(msg);
    throw new Error(msg);
  }
  return cart;
}
window.mmAddToCart = mmAddToCart;

async function setPaymentPreference(value) {
  try {
    await fetch(URLS.update, {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: { 'Payment Preference': value } }),
    });
  } catch (e) { /* not critical */ }
}

/* Goes to Shopify's own checkout (Razorpay, address autocomplete etc. all work there). */
async function goCheckout(mode, discount) {
  await setPaymentPreference(mode === 'cod' ? 'COD' : 'Prepaid');
  if (mode !== 'cod' && discount) {
    window.location.href = `${ROOT}discount/${encodeURIComponent(discount)}?redirect=${encodeURIComponent('/checkout')}`;
  } else {
    window.location.href = URLS.checkout;
  }
}

/* Add buttons: data-mm-add="variantId" or "selected" (main product), data-mm-mode="cart|buy|cod" */
document.addEventListener('click', async (e) => {
  const btn = e.target.closest('[data-mm-add]');
  if (!btn || btn.disabled) return;
  e.preventDefault();
  if (btn.classList.contains('is-loading')) return;
  const scope = btn.closest('[data-mm-product]') || document;
  let id = btn.dataset.mmAdd;
  const isMain = id === 'selected';
  if (isMain) {
    const vSel = $('[data-mm-variant]', scope);
    id = vSel ? vSel.value : '';
  }
  if (!id) { toast('Please select an option'); return; }
  let qty = parseInt(btn.dataset.mmQty, 10) || 1;
  if (isMain) {
    const qEl = $('[data-mm-qty-val]', scope);
    if (qEl) qty = parseInt(qEl.textContent, 10) || 1;
  }
  const items = [{ id, quantity: qty }];
  if (isMain) {
    const addon = $('[data-mm-addon]', scope) || $('.mm-pdp [data-mm-addon]');
    if (addon && addon.checked && addon.value) items.push({ id: addon.value, quantity: 1 });
  }
  const mode = btn.dataset.mmMode || 'cart';
  btn.classList.add('is-loading');
  btn.setAttribute('aria-busy', 'true');
  try {
    await mmAddToCart(items, btn);
    if (mode === 'buy' || mode === 'cod') await goCheckout(mode, btn.dataset.mmDiscount || '');
  } catch (err) { /* message already shown */ }
  finally { btn.classList.remove('is-loading'); btn.removeAttribute('aria-busy'); }
});

/* Fallback: if someone presses Enter inside the product form, use the same flow. */
document.addEventListener('submit', (e) => {
  const f = e.target.closest('[data-mm-form]');
  if (!f) return;
  e.preventDefault();
  const b = $('[data-mm-add="selected"]', f);
  if (b) b.click();
});

/* Quantity steppers */
document.addEventListener('click', (e) => {
  const b = e.target.closest('[data-mm-step]');
  if (!b) return;
  const scope = b.closest('[data-mm-product]') || document;
  let n = 1;
  $$('[data-mm-qty-val]', scope).forEach((v) => { n = Math.max(1, Math.min(99, (parseInt(v.textContent, 10) || 1) + Number(b.dataset.mmStep))); v.textContent = n; });
  $$('[data-mm-qty-input]', scope).forEach((i) => (i.value = n));
});

/* Copy coupon */
document.addEventListener('click', (e) => {
  const b = e.target.closest('[data-mm-copy]');
  if (!b) return;
  const c = b.dataset.mmCopy;
  const done = () => toast(`Code ${c} copied ✓ – apply it at checkout`);
  if (navigator.clipboard && window.isSecureContext) navigator.clipboard.writeText(c).then(done).catch(() => toast(`Code: ${c}`));
  else {
    const ta = document.createElement('textarea'); ta.value = c; ta.style.position = 'fixed'; ta.style.opacity = '0';
    document.body.appendChild(ta); ta.select();
    try { document.execCommand('copy'); done(); } catch (err) { toast(`Code: ${c}`); }
    ta.remove();
  }
  b.textContent = 'Copied ✓';
  setTimeout(() => (b.textContent = 'Copy'), 2000);
});

/* ---------------- Variant change: re-render the product section from Shopify ----------------
   Price, compare price, stock, offers, buttons and metafields all update correctly. */
document.addEventListener('change', async (e) => {
  const sel = e.target.closest('[data-mm-rerender]');
  if (!sel) return;
  const sectionEl = sel.closest('.shopify-section');
  if (!sectionEl) return;
  const sectionId = sectionEl.id.replace('shopify-section-', '');
  const variantId = sel.value;
  const qty = $('[data-mm-qty-val]', sectionEl)?.textContent || '1';
  sectionEl.classList.add('mm-is-updating');
  try {
    const url = `${window.location.pathname}?variant=${encodeURIComponent(variantId)}&section_id=${encodeURIComponent(sectionId)}`;
    const html = await fetch(url, { credentials: 'same-origin' }).then((r) => { if (!r.ok) throw new Error(r.status); return r.text(); });
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.getElementById(sectionEl.id) || doc.querySelector('.shopify-section');
    if (!fresh) throw new Error('section not found');
    cleanupFloating(sectionEl);
    sectionEl.innerHTML = fresh.innerHTML;
    $$('[data-mm-qty-val]', sectionEl).forEach((v) => (v.textContent = qty));
    $$('[data-mm-qty-input]', sectionEl).forEach((i) => (i.value = qty));
    const u = new URL(window.location.href); u.searchParams.set('variant', variantId);
    window.history.replaceState({}, '', u.toString());
    init(sectionEl);
    $('[data-mm-rerender]', sectionEl)?.focus({ preventScroll: true });
  } catch (err) {
    window.location.href = `${window.location.pathname}?variant=${encodeURIComponent(variantId)}`;
  } finally { sectionEl.classList.remove('mm-is-updating'); }
});

/* ---------------- Simple variant select (blog buy cards, featured product) ---------------- */
document.addEventListener('change', (e) => {
  const sel = e.target.closest('[data-mm-vsel]');
  if (!sel) return;
  const scope = sel.closest('[data-mm-product]') || document;
  const o = sel.selectedOptions[0];
  if (o && o.dataset.price) $$('[data-mm-vprice]', scope).forEach((el) => (el.textContent = o.dataset.price));
});

/* ---------------- Gallery ---------------- */
function initGallery(root) {
  if (root.dataset.mmInit) return; root.dataset.mmInit = '1';
  const main = $('[data-mm-gal-main]', root);
  const thumbs = $$('[data-mm-gal-thumb]', root);
  if (!main || !thumbs.length) return;
  let i = Math.max(0, thumbs.findIndex((t) => t.classList.contains('is-on')));
  const set = (n) => {
    i = (n + thumbs.length) % thumbs.length;
    const t = thumbs[i];
    main.src = t.dataset.src; main.srcset = t.dataset.srcset || '';
    thumbs.forEach((x, j) => x.classList.toggle('is-on', j === i));
  };
  thumbs.forEach((t, j) => t.addEventListener('click', () => set(j)));
  $('[data-mm-gal-prev]', root)?.addEventListener('click', () => set(i - 1));
  $('[data-mm-gal-next]', root)?.addEventListener('click', () => set(i + 1));
  let sx = 0;
  main.addEventListener('touchstart', (e) => (sx = e.touches[0].clientX), { passive: true });
  main.addEventListener('touchend', (e) => { const dx = e.changedTouches[0].clientX - sx; if (Math.abs(dx) > 40) set(i + (dx < 0 ? 1 : -1)); });
}

/* ---------------- Countdown to dispatch cut-off (India time) ---------------- */
function tick() {
  $$('[data-mm-cd]').forEach((el) => {
    const h = parseInt(el.dataset.mmCd || '15', 10);
    const n = new Date(); const t = new Date(); t.setHours(h, 0, 0, 0); if (n > t) t.setDate(t.getDate() + 1);
    const s = Math.floor((t - n) / 1000);
    el.textContent = `${Math.floor(s / 3600)}h ${String(Math.floor((s % 3600) / 60)).padStart(2, '0')}m ${String(s % 60).padStart(2, '0')}s`;
  });
}

/* ---------------- Pincode delivery estimate (approximate, no API) ---------------- */
document.addEventListener('submit', (e) => {
  const f = e.target.closest('[data-mm-pin]');
  if (!f) return;
  e.preventDefault();
  const v = $('input', f).value.trim();
  const out = $('[data-mm-pin-out]', f.parentElement);
  if (!out) return;
  out.classList.add('is-on');
  if (!/^[1-9]\d{5}$/.test(v)) { out.innerHTML = '<div class="mm-err">Please enter a valid 6-digit pincode</div>'; return; }
  const fmt = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' }); };
  const range = (s) => String(s || '').split('-').map((x) => parseInt(x, 10)).filter((x) => !Number.isNaN(x));
  const p = range(f.dataset.prepaid); const c = range(f.dataset.cod);
  const esc = (s) => String(s).replace(/[&<>"]/g, (m) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[m]));
  let html = `<div>✅ <span>Shipping to <b>${esc(v)}</b> via ${esc(f.dataset.courier || 'courier')}</span></div>`;
  if (p.length) html += `<div>⚡ <span><b>Prepaid:</b> expected by ${fmt(p[p.length - 1])}</span></div>`;
  if (c.length) html += `<div>📦 <span><b>COD:</b> ${fmt(c[0])} – ${fmt(c[c.length - 1])}</span></div>`;
  out.innerHTML = html;
});

/* ---------------- Video modal ---------------- */
function videoModal() {
  let m = $('.mm-vmodal');
  if (m) return m;
  m = document.createElement('div');
  m.className = 'mm-vmodal mm';
  m.setAttribute('role', 'dialog'); m.setAttribute('aria-modal', 'true'); m.setAttribute('aria-label', 'Video');
  m.innerHTML = '<button class="mm-vmodal__x" aria-label="Close">×</button><div class="mm-vbox"><video controls playsinline preload="none"></video><div class="mm-vbar" hidden></div></div>';
  document.body.appendChild(m);
  const close = () => { const v = $('video', m); v.pause(); v.removeAttribute('src'); v.load(); m.classList.remove('is-open'); };
  m.addEventListener('click', (e) => { if (e.target === m || e.target.closest('.mm-vmodal__x')) close(); });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && m.classList.contains('is-open')) close(); });
  return m;
}
document.addEventListener('click', (e) => {
  const r = e.target.closest('[data-mm-video]');
  if (!r || !r.dataset.mmVideo) return;
  e.preventDefault();
  const m = videoModal(); const v = $('video', m); const bar = $('.mm-vbar', m);
  v.src = r.dataset.mmVideo;
  const tpl = r.parentElement.querySelector('template[data-mm-vbar]');
  if (tpl) { bar.innerHTML = tpl.innerHTML; bar.hidden = false; } else { bar.innerHTML = ''; bar.hidden = true; }
  m.classList.add('is-open');
  v.play().catch(() => {});
  $('.mm-vmodal__x', m).focus({ preventScroll: true });
});
document.addEventListener('click', (e) => { if (e.target.closest('.mm-vbar [data-mm-add]')) { const m = $('.mm-vmodal'); if (m) $('video', m).pause(); } });

/* ---------------- Hero slider ---------------- */
function initSlider(root) {
  if (root.dataset.mmInit) return; root.dataset.mmInit = '1';
  const track = $('[data-mm-slides]', root); const dots = $('[data-mm-dots]', root);
  if (!track) return;
  const slides = $$('.mm-slide', track);
  if (slides.length < 2) { if (dots) dots.hidden = true; return; }
  let i = 0; let timer = null;
  dots.innerHTML = slides.map((_, j) => `<button type="button" aria-label="Slide ${j + 1}"></button>`).join('');
  const go = (n) => { i = (n + slides.length) % slides.length; track.style.transform = `translateX(-${i * 100}%)`; $$('button', dots).forEach((b, j) => b.classList.toggle('is-on', j === i)); slides.forEach((s, j) => s.setAttribute('aria-hidden', j === i ? 'false' : 'true')); };
  $$('button', dots).forEach((b, j) => b.addEventListener('click', () => go(j)));
  go(0);
  const ms = (parseInt(root.dataset.mmAutoplay, 10) || 0) * 1000;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const start = () => { if (ms && !reduce && !window.Shopify?.designMode) { clearInterval(timer); timer = setInterval(() => go(i + 1), ms); } };
  const stop = () => clearInterval(timer);
  start();
  root.addEventListener('mouseenter', stop); root.addEventListener('mouseleave', start);
  root.addEventListener('focusin', stop); root.addEventListener('focusout', start);
  let sx = 0;
  track.addEventListener('touchstart', (e) => { sx = e.touches[0].clientX; stop(); }, { passive: true });
  track.addEventListener('touchend', (e) => { const dx = e.changedTouches[0].clientX - sx; if (Math.abs(dx) > 50) go(i + (dx < 0 ? 1 : -1)); start(); });
  root.addEventListener('shopify:block:select', (e) => { const k = slides.indexOf(e.target.closest('.mm-slide')); if (k > -1) { stop(); go(k); } });
}

/* ---------------- Floating order card (moved to <body> so it can be fixed) ---------------- */
function cleanupFloating(sectionEl) {
  const id = sectionEl.id;
  $$(`[data-mm-ocard][data-mm-owner="${id}"]`).forEach((c) => c.remove());
  document.body.classList.remove('mm-oc-on');
}
function initOcard(root) {
  const card = $('[data-mm-ocard]', root); const anchor = $('[data-mm-ocard-anchor]', root);
  if (!card || !anchor) return;
  const sectionEl = root.closest('.shopify-section');
  if (sectionEl) card.dataset.mmOwner = sectionEl.id;
  document.body.appendChild(card);
  let closed = false;
  $('[data-mm-ocard-close]', card)?.addEventListener('click', () => { closed = true; card.classList.remove('is-on'); document.body.classList.remove('mm-oc-on'); });
  new IntersectionObserver(([en]) => {
    const on = !closed && !en.isIntersecting && en.boundingClientRect.top < 0;
    card.classList.toggle('is-on', on); document.body.classList.toggle('mm-oc-on', on);
  }).observe(anchor);
}

/* ---------------- SEO read more ---------------- */
document.addEventListener('click', (e) => {
  const b = e.target.closest('[data-mm-more]');
  if (!b) return;
  const t = document.getElementById(b.dataset.mmMore);
  if (!t) return;
  const open = t.classList.toggle('is-open');
  b.textContent = open ? b.dataset.less : b.dataset.more;
  b.setAttribute('aria-expanded', String(open));
});

/* ---------------- Article table of contents ---------------- */
function initToc(root) {
  const prose = $('[data-mm-prose]', root); const toc = $('[data-mm-toc]', root);
  if (!prose || !toc || toc.dataset.mmInit) return;
  toc.dataset.mmInit = '1';
  const hs = $$('h2, h3', prose).slice(0, 10);
  if (hs.length < 2) { toc.hidden = true; return; }
  const ol = toc.querySelector('ol');
  ol.innerHTML = '';
  hs.forEach((h, i) => {
    h.id = h.id || `mm-s${i}`;
    const li = document.createElement('li'); const a = document.createElement('a');
    a.href = `#${h.id}`; a.textContent = h.textContent.trim(); li.appendChild(a); ol.appendChild(li);
  });
}

/* ---------------- Track order (Delhivery) ---------------- */
document.addEventListener('submit', (e) => {
  const f = e.target.closest('[data-mm-track]');
  if (!f) return;
  e.preventDefault();
  const awb = $('input', f).value.replace(/\s/g, '');
  if (awb.length < 6) { toast('Please enter a valid tracking number'); return; }
  window.open(f.dataset.url.replace('{awb}', encodeURIComponent(awb)), '_blank', 'noopener');
});
document.addEventListener('click', (e) => {
  const b = e.target.closest('[data-mm-tab]');
  if (!b) return;
  const root = b.closest('[data-mm-tabs-root]');
  if (!root) return;
  $$('[data-mm-tab]', root).forEach((x) => { x.classList.toggle('is-on', x === b); x.setAttribute('aria-selected', String(x === b)); });
  $$('[data-mm-pane]', root).forEach((p) => (p.hidden = p.dataset.mmPane !== b.dataset.mmTab));
});

/* ---------------- Collection: sort & filter auto-submit ---------------- */
document.addEventListener('submit', (e) => {
  const f = e.target.closest('[data-mm-filters]');
  if (!f) return;
  /* don't send empty price boxes (Shopify would read them as 0) */
  $$('input[type="number"]', f).forEach((i) => { if (i.value === '') i.disabled = true; });
});
document.addEventListener('change', (e) => {
  const el = e.target.closest('[data-mm-autosubmit]');
  if (!el || !el.form) return;
  el.form.requestSubmit ? el.form.requestSubmit() : el.form.submit();
});

/* ---------------- Reviews: write review ---------------- */
document.addEventListener('click', (e) => {
  const scroll = e.target.closest('[data-mm-scroll-reviews]');
  if (scroll) { const t = document.getElementById('mm-reviews'); if (t) { e.preventDefault(); t.scrollIntoView({ behavior: 'smooth', block: 'start' }); } return; }
  const w = e.target.closest('[data-mm-write-review]');
  if (w) {
    e.preventDefault();
    const jdgm = document.querySelector('.jdgm-write-rev-link');
    if (jdgm) { jdgm.click(); const f = document.querySelector('.jdgm-form-wrapper, .jdgm-form'); (f || jdgm).scrollIntoView({ behavior: 'smooth', block: 'center' }); return; }
    const dlg = w.closest('section')?.querySelector('[data-mm-review-dialog]') || $('[data-mm-review-dialog]');
    if (dlg && dlg.showModal) dlg.showModal();
    return;
  }
  const c = e.target.closest('[data-mm-close]');
  if (c) { const d = c.closest('dialog'); if (d) d.close(); }
});
document.addEventListener('click', (e) => { if (e.target.matches && e.target.matches('dialog[data-mm-review-dialog]')) e.target.close(); });
/* re-open the review form after it was posted so the customer sees the thank-you message */
if (/contact_posted=true/.test(location.search) && location.hash === '#mm-review-form') { const d = $('[data-mm-review-dialog]'); if (d && d.showModal) d.showModal(); }

/* ---------------- Init (also runs again inside the theme editor) ---------------- */
function init(scope = document) {
  $$('[data-mm-gallery]', scope).forEach(initGallery);
  $$('[data-mm-slider]', scope).forEach(initSlider);
  $$('[data-mm-product]', scope).filter((el) => !el.matches('[data-mm-ocard]')).forEach(initOcard);
  $$('[data-mm-article]', scope).forEach(initToc);
  tick();
}
init();
setInterval(tick, 1000);
document.addEventListener('shopify:section:load', (e) => init(e.target));
document.addEventListener('shopify:section:unload', (e) => cleanupFloating(e.target));
